// Remove (line item) button inside the cart drawer
class CartDrawerRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartDrawer = this.closest('cart-drawer');
      cartDrawer.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-drawer-remove-button', CartDrawerRemoveButton);

// Main cart drawer component
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = document.getElementById('cart-drawer');
    this.cartDrawerOpen = document.querySelector('[data-js-cart-drawer="cartDrawerOpen"]');
    this.cartDrawerClose = document.querySelector('[data-js-cart-drawer="cartDrawerClose"]');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.itemCountHolder = document.querySelector('[data-js-cart-item-count]');
    this.lineItemStatusElement = document.getElementById('cart-drawer-line-item-status');

    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value || 0, 10), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    if (this.drawer) {
      this.drawer.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    }

    this.querySelectorAll('[data-js-cart-drawer="cartDrawerClose"]').forEach((closeButton) => {
      this.activeElement = document.activeElement || this.cartDrawerOpen;
      closeButton.addEventListener('click', this.close.bind(this));
    });

    document.querySelectorAll('[data-js-cart-drawer="cartDrawerOpen"]').forEach((openButton) => {
      openButton.addEventListener('click', this.handleDrawerOpen.bind(this));
    });
  }

  onChange(event) {
    const idx = event.target && event.target.dataset && event.target.dataset.index;
    const value = event.target && event.target.value;
    this.updateQuantity(idx, value, document.activeElement && document.activeElement.getAttribute('name'));
  }

  handleDrawerOpen() {
    if (typeof theme !== 'undefined' && theme.pageType === 'cart') return;
    this.open();
  }

  // Always fetch fresh sections after add-to-cart
  handleAddedItem() {
    this.fetchAndRenderSections().catch((e) => console.error('Cart drawer refresh failed', e));
  }

  getSectionsToRender() {
    // section handles (Liquid) and DOM ids/selectors used by renderContents
    const sections = [];

    const drawerEl = document.getElementById('cart-drawer');
    if (drawerEl && drawerEl.dataset && drawerEl.dataset.id) {
      sections.push({
        id: 'cart-drawer',
        section: drawerEl.dataset.id,
        selector: '.js-contents'
      });
    } else {
      // fallback: still include cart-drawer as a section handle if possible
      sections.push({
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.js-contents'
      });
    }

    // cart-icon-bubble may not exist in all themes; include as a section handle but be defensive
    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '#cart-icon-bubble'
    });

    return sections;
  }

  updateQuantity(line, quantity, name) {
    // normalize line and quantity to integers where possible
    const lineNum = line ? Number(line) : line;
    const qtyNum = (quantity === '' || quantity === null || typeof quantity === 'undefined') ? quantity : Number(quantity);

    this.enableLoading(lineNum);

    const body = JSON.stringify({
      line: lineNum,
      quantity: qtyNum,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (err) {
          // If parsing fails, force a defensive refresh and return
          return this.fetchAndRenderSections();
        }

        // If Shopify didn’t send sections (some responses don't), force refresh
        if (!parsedState || !parsedState.sections) {
          return this.fetchAndRenderSections();
        }

        // Render using what we got
        try {
          this.renderContents(parsedState, false, lineNum, name, true);
        } catch (err) {
          // If render fails for any reason, fallback to a full refresh
          return this.fetchAndRenderSections();
        }

        // Extra refresh to keep drawer + bubble fully synced
        this.fetchAndRenderSections().catch((e) => {
          console.error('Cart refresh after change failed', e);
        });
      })
      .catch((err) => {
        // Never leave drawer stuck: force a full refresh on network error
        console.error('Cart change request failed', err);
        this.fetchAndRenderSections().catch((e) => {
          console.error('Cart refresh failed after error', e);
        });
      });
  }

  open() {
    this.activeElement = document.activeElement || this.cartDrawerOpen;
    if (this.drawer) {
      this.drawer.classList.add('animate', 'active');
      this.drawer.addEventListener('transitionend', () => {
        this.drawer.focus();
        if (typeof trapFocus === 'function') trapFocus(this.drawer, this.cartDrawerClose);
      }, { once: true });
      document.body.addEventListener('click', this.onBodyClick);
    }
  }

  close() {
    if (this.drawer) {
      this.drawer.classList.remove('active');
    }
    document.body.removeEventListener('click', this.onBodyClick);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState, addedItemResponse = false, line, name, isDrawerOpened = false) {
    try {
      // item_count safety
      const itemCount = parsedState && typeof parsedState.item_count === 'number' ? parsedState.item_count : 0;
      this.classList.toggle('is-empty', itemCount === 0);

      if (this.itemCountHolder) {
        this.itemCountHolder.innerText = itemCount;
        this.itemCountHolder.classList.toggle('is-active', itemCount > 0);
      }

      const cartFooter = document.getElementById('cart-drawer-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);

      const sections = this.getSectionsToRender();

      sections.forEach((section) => {
        try {
          // safely find the container we should replace
          let container = null;
          const sectionRoot = document.getElementById(section.id);
          if (sectionRoot) {
            container = sectionRoot.querySelector(section.selector) || sectionRoot;
          } else {
            // fallback to selector anywhere in DOM (may be header bubble)
            container = document.querySelector(section.selector);
          }

          if (!container) {
            // nothing to update for this section in this theme — skip it
            return;
          }

          if (parsedState && parsedState.sections && parsedState.sections[section.section]) {
            const htmlFragment = parsedState.sections[section.section];
            // insert only the inner part that matches the selector, but be defensive
            try {
              const parsed = new DOMParser().parseFromString(htmlFragment, 'text/html');
              const inner = parsed.querySelector(section.selector) || parsed.body;
              container.innerHTML = inner ? inner.innerHTML : htmlFragment;
            } catch (err) {
              // fallback: insert the raw HTML fragment
              container.innerHTML = htmlFragment;
            }
          }
        } catch (err) {
          // Continue to next section even if one fails
          console.error('Error updating a cart section', section, err);
        }
      });

      // update live regions and focus
      this.updateLiveRegions(line, itemCount);

      const lineItem = document.getElementById(`CartDrawerItem-${line}`);
      if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
        lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } catch (err) {
      // swallow and continue — we will always ensure loading gets disabled below
      console.error('renderContents top-level error', err);
    } finally {
      // ALWAYS remove loading state so nothing stays greyed out
      this.disableLoading();
      if (!isDrawerOpened) {
        if (this.header && this.header.reveal) this.header.reveal();
        this.open();
      }
    }
  }

  getSectionInnerHTML(html, selector) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector(selector);
      return el ? el.innerHTML : html;
    } catch (e) {
      return html;
    }
  }

  updateLiveRegions(line, itemCount) {
    if (typeof line === 'undefined' || line === null) return;

    if (this.currentItemCount === itemCount) {
      const errWrap = document.getElementById(`CartDrawerItem-error-${line}`);
      const qtyInput = document.getElementById(`CartDrawerItem-quantity-${line}`);
      if (errWrap && qtyInput && window.cartStrings && window.cartStrings.quantityError) {
        const errText = errWrap.querySelector('.cart-item__error-text');
        if (errText) {
          errText.innerHTML = window.cartStrings.quantityError.replace('[quantity]', qtyInput.value);
        }
      }
    }

    this.currentItemCount = itemCount;
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus = document.getElementById('cart-drawer-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  // FIXED: Prevents drawer locking or greying out
  enableLoading(line) {
    const root = document.getElementById('cart-drawer');
    if (root) root.classList.add('cart-drawer--disabled');

    // Show per-item overlay if present
    if (line !== undefined && line !== null) {
      this.querySelectorAll(`#CartDrawerItem-${line} .loading-overlay`)
        .forEach((overlay) => overlay.classList.remove('hidden'));
    }

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  // FIXED: Remove ALL stuck loading overlays so you can remove multiple items
  disableLoading() {
    const root = document.getElementById('cart-drawer');
    if (root) root.classList.remove('cart-drawer--disabled');

    // Hide any loading overlays if present
    this.querySelectorAll('.loading-overlay')
      .forEach((overlay) => overlay.classList.add('hidden'));
  }

  handleBodyClick(event) {
    const target = event.target;
    const isNotDrawer = (target !== this.drawer) && (!target.closest('cart-drawer'));
    const isNotButtonOpen = target !== this.cartDrawerOpen && !target.closest('[data-js-cart-drawer="cartDrawerOpen"]');
    const isCloseButton = (target === this.cartDrawerClose) || (target.closest('[data-js-cart-drawer="cartDrawerClose"]'));

    if ((isNotDrawer && isNotButtonOpen) || isCloseButton) {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateTrappedFocus() {
    if (typeof removeTrapFocus === 'function') removeTrapFocus(document.activeElement);
    setTimeout(() => {
      this.setActiveElement(this.cartDrawerClose || this.cartDrawerOpen);
      if (this.drawer) {
        this.drawer.focus();
        if (typeof trapFocus === 'function') trapFocus(this.drawer, this.cartDrawerClose);
      }
    });
  }

  // Fetch latest sections (drawer + bubble) and cart count, then re-render
  fetchAndRenderSections() {
    return new Promise((resolve, reject) => {
      const sections = this.getSectionsToRender();
      const handles = sections.map(s => s.section);
      const url = `${routes.cart_url}?sections=${handles.join(',')}&sections_url=${encodeURIComponent(window.location.pathname)}`;

      Promise.all([
        fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.json()),
        fetch('/cart.js').then(r => r.json())
      ])
        .then(([sectionsJson, cartJson]) => {
          const parsedState = {
            item_count: cartJson && typeof cartJson.item_count === 'number' ? cartJson.item_count : 0,
            sections: sectionsJson || {}
          };
          this.renderContents(parsedState, false, '', '', false);
          resolve();
        })
        .catch((err) => {
          console.error('fetchAndRenderSections failed', err);
          // ensure we don't leave the drawer stuck
          this.disableLoading();
          reject(err);
        });
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
